import React, { useRef, useEffect, useState } from 'react';

interface InpaintingCanvasProps {
    imageUrl: string;
    onChange: (maskImage: string | null) => void;

    brushSize?: number;
    enabled?: boolean;
    className?: string;
}

export function InpaintingCanvas({
    imageUrl,
    onChange,

    brushSize = 20,
    enabled = false,
    className = '',
}: InpaintingCanvasProps): React.JSX.Element {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showCursor, setShowCursor] = useState(false);
    const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });

    // Initialize canvas context
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const context = canvas.getContext('2d');
        if (!context) return;

        setCtx(context);
    }, []);

    // ResizeObserver logic to sync canvas with image size
    useEffect(() => {
        const image = imageRef.current;
        const canvas = canvasRef.current;
        if (!image || !canvas) return;

        const resizeCanvas = () => {
            const rect = image.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) return;

            // Set internal resolution to match display size
            canvas.width = rect.width;
            canvas.height = rect.height;

            // Note: Resizing clears the canvas. 
        };

        if (image.complete) {
            resizeCanvas();
        } else {
            image.onload = resizeCanvas;
        }

        const observer = new ResizeObserver(() => resizeCanvas());
        observer.observe(image);

        return () => observer.disconnect();
    }, [imageUrl]);

    const getCoordinates = (event: React.PointerEvent) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };

        const rect = canvas.getBoundingClientRect();

        // Map visual coordinate to internal canvas coordinate
        return {
            x: (event.clientX - rect.left) * (canvas.width / rect.width),
            y: (event.clientY - rect.top) * (canvas.height / rect.height)
        };
    };

    const handlePointerDown = (e: React.PointerEvent) => {
        if (!enabled) return;

        e.preventDefault();
        e.stopPropagation();
        (e.target as Element).setPointerCapture(e.pointerId);

        setIsDrawing(true);
        const { x, y } = getCoordinates(e);

        if (ctx) {
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.fillStyle = 'rgba(255, 0, 0, 0.5)';
            ctx.arc(x, y, brushSize / 2, 0, Math.PI * 2);
            ctx.fill();
            ctx.beginPath(); // Start new path for drag
            ctx.moveTo(x, y);
        }
    };

    const handlePointerMove = (e: React.PointerEvent) => {
        // Update cursor position
        if (enabled) {
            const { x, y } = getCoordinates(e);
            setCursorPosition({ x, y });
        }

        if (!enabled || !isDrawing || !ctx) return;

        e.preventDefault();
        e.stopPropagation();

        const { x, y } = getCoordinates(e);

        ctx.lineWidth = brushSize;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = 'rgba(255, 0, 0, 0.5)';

        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const handlePointerUp = (e: React.PointerEvent) => {
        if (!isDrawing) return;

        e.preventDefault();
        (e.target as Element).releasePointerCapture(e.pointerId);
        setIsDrawing(false);

        if (ctx) {
            ctx.closePath();
        }

        // Export mask
        if (canvasRef.current) {
            const dataUrl = canvasRef.current.toDataURL('image/png');
            onChange(dataUrl);
        }
    };

    const toggleFullscreen = () => {
        if (!isFullscreen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        setIsFullscreen(!isFullscreen);
    };

    return (
        <div
            ref={containerRef}
            className={`relative flex items-center justify-center ${isFullscreen ? 'fixed inset-0 z-50 bg-black' : className}`}
            onPointerEnter={(e) => e.pointerType === 'mouse' && setShowCursor(true)}
            onPointerLeave={() => setShowCursor(false)}
        >
            {/* Wrapper to constrain canvas to image size */}
            <div className="relative inline-block max-w-full max-h-full">
                <img
                    ref={imageRef}
                    src={imageUrl}
                    className="block max-w-full max-h-full object-contain select-none"
                    style={{
                        maxHeight: isFullscreen ? '100vh' : '100%',
                        maxWidth: isFullscreen ? '100vw' : '100%'
                    }}
                    alt="Workspace"
                    draggable={false}
                />

                {/* Drawing Canvas */}
                <canvas
                    ref={canvasRef}
                    className={`absolute top-0 left-0 w-full h-full touch-none ${enabled ? 'cursor-none' : 'cursor-default'}`}
                    style={{
                        touchAction: 'none'
                    }}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={handlePointerUp}
                />

                {/* Custom Brush Cursor */}
                {enabled && showCursor && (
                    <div
                        style={{
                            position: 'absolute',
                            left: cursorPosition.x,
                            top: cursorPosition.y,
                            width: brushSize,
                            height: brushSize,
                            border: '2px solid white',
                            backgroundColor: 'rgba(255, 0, 0, 0.2)',
                            borderRadius: '50%',
                            pointerEvents: 'none',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 10
                        }}
                    />
                )}
            </div>

            {/* Controls */}
            {enabled && (
                <div className="absolute bottom-4 right-4 flex gap-2 z-20">
                    <button
                        type="button"
                        onClick={() => {
                            if (ctx && canvasRef.current) {
                                ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                                onChange(null);
                            }
                        }}
                        className="bg-white/90 text-red-600 px-3 py-1 rounded-full text-sm font-medium shadow-sm backdrop-blur-sm"
                    >
                        Clear
                    </button>
                    <button
                        type="button"
                        onClick={toggleFullscreen}
                        className="bg-white/90 text-gray-900 px-3 py-1 rounded-full text-sm font-medium shadow-sm backdrop-blur-sm"
                    >
                        {isFullscreen ? 'Done' : 'Expand'}
                    </button>
                </div>
            )}
        </div>
    );
}
